import { Instance, SnapshotOut, types } from "mobx-state-tree"
import { Country, Region, ShrimpPrice } from "../../database"
import { withEnvironment } from "../extensions/with-environment"
import { withRootStore } from "../extensions/with-root-store"
import { withBaseModel } from "../extensions/with-base-model"
import SyncableModel from "../../database/SyncableModel"
import { BaseModelStoreProps } from "../base-model-store/base-model-store"
import { Platform } from "react-native"
import { Toast } from "native-base"
import moment from "moment"
import JalaAnalytic from "../../utils/jala-analytic"

const ShrimpPriceStoreProps = {
  ...BaseModelStoreProps,

  country: types.maybeNull(types.frozen()),
  shrimpPriceId: types.maybeNull(types.number),
  shrimpPrices: types.optional(types.array(types.frozen()), []),
  // editPrice: types.optional(types.frozen(), {}),

  pageShrimpPrice: types.maybeNull(types.number),
  filterShrimpPrice: types.optional(types.frozen(), {}),

  queryingShrimpPrice: types.maybeNull(types.boolean),
  syncingShrimpPrice: types.maybeNull(types.boolean),
  syncedShrimpPrice: types.maybeNull(types.boolean),

  baseData: types.maybeNull(types.frozen()),
  region: types.optional(types.frozen(), {})
}

/**
 * Model description here for TypeScript hints.
 */
export const ShrimpPriceStoreModel = types
  .model("ShrimpPriceStore")
  .props(ShrimpPriceStoreProps)
  .extend(withEnvironment)
  .extend(withRootStore)
  .extend(withBaseModel)
  .views((self) => ({
    get shrimpPrice() {
      return self.shrimpPrices.find((shrimpPrice) => shrimpPrice.id === self.shrimpPriceId)
    },
    get loadingShrimpPrice() {
      return self.queryingShrimpPrice || self.syncingShrimpPrice
    },
  })) // eslint-disable-line @typescript-eslint/no-unused-vars
  .actions((self) => ({
    set(key: keyof typeof ShrimpPriceStoreProps, value: any) {
      if (self[key] === value) return
      if (key === 'filterShrimpPrice')
        this.setFilterShrimpPrice(value)
      else self[key] = value as never;
    },
    setFilterShrimpPrice(value: any) {
      // this.set('shrimpPrices', []);
      self.filterShrimpPrice = value;
    },
    init() {
      self.initModel();
      Country.fetch({
        id__eq: self.filterShrimpPrice.country_id || 'ID'
      }).then((countries) => this.set('country', countries[0] || null))
      this.getShrimpPrices().catch();
    },
    async getShrimpPrices(sync = true, onFinishSync = undefined) {
      if (!sync) {
        this.set('queryingShrimpPrice', true);
        const models = await ShrimpPrice.fetch({
          country_id__eq: self.filterShrimpPrice.country_id || 'ID',
          region_id__begins_with: self.filterShrimpPrice.region_id || undefined,
          [`size_${self.filterShrimpPrice.size}__gt`]: self.filterShrimpPrice.size ? 0 : undefined,
          // 'creator.buyer__not_null': self.filterShrimpPrice.verified === 'verified' ? 1 : undefined,
          date__lte: ['week', 'month'].includes(self.filterShrimpPrice.time) && moment().subtract(1, self.filterShrimpPrice.time).format('YYYY-MM-DD') || undefined,
          with: 'region,creator,country,currency',
          has: 'creator',
          appends: 'shrimp_price_per_week_region_id',
          sort: 'date,desc',
          per_page: 100,
          private__ne_or_null: 1
        }).catch((e) => {
          console.warn(e)
          self.rootStore.appStore.set("isOffline", true)
          this.set('error', e);
          return []
        }).then((models) => {
          if (self.filterShrimpPrice.verified === 'verified')
            return models.filter((m) => (m.creator || {}).buyer)
          return models;
        })
        this.set('shrimpPrices', models.map((model) => model.toArray()));
        this.set('pageShrimpPrice', 2);
        this.set('queryingShrimpPrice', false);
      }
      else {
        this.set('queryingShrimpPrice', true);
        this.syncShrimpPrices().then(async () => {
          await this.getShrimpPrices(false).catch()
          onFinishSync && onFinishSync()
          this.set('syncedShrimpPrice', true);
          this.set('queryingShrimpPrice', false);
        })
      }
    },
    async syncShrimpPrices() {
      if (Platform.OS === 'web') return true;

      this.set('syncingShrimpPrice', true)
      const success = await ShrimpPrice.syncAll(
        {},
        [], true, undefined, (e) => {
          if (e.message && !Toast.isActive(e.name)) Toast.show({ id: e.name, title: e.message });
        }
      )

      this.set('syncingShrimpPrice', false)
      return success
    },
    async getShrimpPricesNext() {
      this.set('queryingShrimpPrice', true);
      const models = await ShrimpPrice.fetch({
        country_id__eq: self.filterShrimpPrice.country_id || 'ID',
        region_id__begins_with: self.filterShrimpPrice.region_id || undefined,
        [`size_${self.filterShrimpPrice.size}__gt`]: self.filterShrimpPrice.size ? 0 : undefined,
        // 'creator.buyer__not_null': self.filterShrimpPrice.verified === 'verified' ? 1 : undefined,
        date__lte: ['week', 'month'].includes(self.filterShrimpPrice.time) && moment().subtract(1, self.filterShrimpPrice.time).format('YYYY-MM-DD') || undefined,
        with: 'region,creator,country,currency',
        has: 'creator',
        appends: 'shrimp_price_per_week_region_id',
        sort: 'date,desc',
        page: self.pageShrimpPrice || 1,
        per_page: 100,
        private__ne_or_null: 1
      }).catch((e) => {
        console.warn(e)
        this.set('error', e);
        return []
      }).then((models) => {
        if (self.filterShrimpPrice.verified === 'verified')
          return models.filter((m) => (m.creator || {}).buyer)
        return models;
      })
      this.set('shrimpPrices', self.shrimpPrices.concat(models.map((model) => model.toArray())))
      this.set('pageShrimpPrice', (self.pageShrimpPrice || 1) + 1);
      this.set('queryingShrimpPrice', false);
    },
    async storeToServer(data, afterPushCallback = undefined) {
      return self.storeModelToServer(ShrimpPrice, data, afterPushCallback)
        .then((success: boolean) => {
          if (success) JalaAnalytic.log('Mobile app: save shrimp price - add', data);
          return success;
        })
    },
    async getRegion(id) {
      Region.fetch({
        id__eq: id
      }).then((regions) => {
        return this.set('region', regions[0] || null)
      })
    },
    async store(data, afterPushCallback = undefined) {
      return this.storeToServer(data, afterPushCallback);
      // return self.storeModel(ShrimpPrice, data);
    },
    async updateToServer(model: SyncableModel, data) {
      return self.updateModelToServer(ShrimpPrice, model, data)
        .then((success: boolean) => {
          if (success) JalaAnalytic.log('Mobile app: save shrimp price - edit', data);
          return success;
        });
    },
    async update(model: SyncableModel, data) {
      return this.updateToServer(model, data);
      // return self.updateModel(ShrimpPrice, model, data);
    },
    async deleteFromServer(model: SyncableModel) {
      return self.deleteModelFromServer(ShrimpPrice, model)
        .then((success: boolean) => {
          if (success) JalaAnalytic.log('Mobile app: delete shrimp price', model.toArray());
          return success;
        });
    },
    async delete(model: SyncableModel) {
      return this.deleteFromServer(model)
      // return self.deleteModel(ShrimpPrice, model)
    },
    async getShrimpPriceById() {
      return await ShrimpPrice.fetch({
        id__eq: self.shrimpPriceId,
        with: 'region,creator,country,currency',
        has: 'creator',
        appends: 'shrimp_price_per_week_region_id',
      }).then((models) => {
        const prices = models.map((price) => price.toArray())
        return prices[0]
      }).catch((e) => {
        console.warn(e)
        this.set('error', e);
        return []
      })
    },
  })) // eslint-disable-line @typescript-eslint/no-unused-vars


type ShrimpPriceStoreType = Instance<typeof ShrimpPriceStoreModel>
export interface ShrimpPriceStore extends ShrimpPriceStoreType {}
type ShrimpPriceStoreSnapshotType = SnapshotOut<typeof ShrimpPriceStoreModel>
export interface ShrimpPriceStoreSnapshot extends ShrimpPriceStoreSnapshotType {}
export const createShrimpPriceStoreDefaultModel = () => types.optional(ShrimpPriceStoreModel, {})
